import type {
  AddressResolveResult,
  BbrAttributes,
  BuildingContextUnit,
  BuildingType,
  ComparableDTO,
  ComparableScoreBreakdown,
  ComparablesAggregate,
  ComparablesQuery,
  ComparablesResponse,
  ComparablesTarget,
  EnergyRating,
  PriceHistoryPoint,
  SparkPointDTO,
  UnitDetailDTO,
  UnitEvent,
  UnitListRow,
  UnitsQuery,
  UnitsResponse
} from './types';

// Deterministic mock generator for the units list. Returns the same rows
// every time so visual diff and golden tests are stable. Real backend lands
// in [DEV-105](/DEV/issues/DEV-105); this client is swapped via VITE_USE_MOCK
// flag inside ./client.ts.

const STREETS = [
  'Vesterbrogade', 'Nørrebrogade', 'Amagerbrogade', 'Strandvejen', 'Frederiksberg Allé',
  'Sønder Boulevard', 'Istedgade', 'Gothersgade', 'Falkoner Allé', 'Tagensvej',
  'Trøjborgvej', 'Skovvejen', 'Mejlgade', 'Vestergade', 'Møllevangs Allé',
  'Klosterport', 'Nørregade', 'Sønder Allé', 'Vestre Strandallé', 'Risskovvej',
  'Reberbansgade', 'Algade', 'Boulevarden', 'Hasserisvej', 'Vesterbro',
  'Karen Brands Vej', 'Skibhusvej', 'Falen', 'Albanigade', 'Hans Egedes Vej'
];

const CITY_BY_PRE: Array<{ pre: string; city: string }> = [
  { pre: '1620', city: 'København V' },
  { pre: '2200', city: 'København N' },
  { pre: '2300', city: 'København S' },
  { pre: '2100', city: 'København Ø' },
  { pre: '1850', city: 'Frederiksberg C' },
  { pre: '8000', city: 'Aarhus C' },
  { pre: '8200', city: 'Aarhus N' },
  { pre: '8230', city: 'Åbyhøj' },
  { pre: '8240', city: 'Risskov' },
  { pre: '5000', city: 'Odense C' },
  { pre: '5230', city: 'Odense M' },
  { pre: '5210', city: 'Odense NV' },
  { pre: '9000', city: 'Aalborg' },
  { pre: '9210', city: 'Aalborg SØ' },
  { pre: '9220', city: 'Aalborg Øst' }
];

const SOURCES: UnitListRow['source'][] = ['BoligPortal', 'Lejebolig', 'Lejeboligportal', 'BoligZonen'];
const STATUSES: UnitListRow['status'][] = ['active', 'active', 'active', 'pending', 'rented', 'delisted'];

// Mulberry32 — small, fast, deterministic PRNG.
function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function buildSpark(rng: () => number, base: number): SparkPointDTO[] {
  const points: SparkPointDTO[] = [];
  let v = base;
  const start = new Date(2025, 4, 1).getTime();
  for (let i = 0; i < 12; i += 1) {
    const drift = (rng() - 0.45) * base * 0.04;
    v = Math.max(base * 0.6, v + drift);
    const ts = new Date(start + i * 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    points.push({ ts, priceKrPerMonth: Math.round(v) });
  }
  return points;
}

function buildRow(rng: () => number, idx: number): UnitListRow {
  const street = STREETS[Math.floor(rng() * STREETS.length)];
  const houseNum = 1 + Math.floor(rng() * 220);
  const floor = 1 + Math.floor(rng() * 5);
  const sides = ['tv', 'th', 'mf'];
  const side = sides[Math.floor(rng() * sides.length)];
  const cityRow = CITY_BY_PRE[Math.floor(rng() * CITY_BY_PRE.length)];
  const m2 = Math.round((38 + rng() * 130) * 10) / 10;
  const rooms = 1 + Math.floor(rng() * 5);
  const yearBuilt = 1880 + Math.floor(rng() * 145);
  const krPerSqm = Math.round(120 + rng() * 200);
  const rentKrPerMonth = Math.round(m2 * krPerSqm);
  const status = STATUSES[Math.floor(rng() * STATUSES.length)];
  const source = SOURCES[Math.floor(rng() * SOURCES.length)];
  const lastSeenDays = Math.floor(rng() * 14);
  const lastSeen = new Date(Date.now() - lastSeenDays * 86400000).toISOString();
  return {
    id: `unit-${idx.toString().padStart(5, '0')}`,
    address: `${street} ${houseNum}, ${floor}. ${side}`,
    city: cityRow.city,
    postnummer: cityRow.pre,
    m2,
    rooms,
    yearBuilt,
    rentKrPerMonth,
    krPerSqm,
    status,
    source,
    lastSeen,
    spark: buildSpark(rng, rentKrPerMonth)
  };
}

let cache: UnitListRow[] | null = null;
function getAll(): UnitListRow[] {
  if (cache) return cache;
  const rng = mulberry32(20260429);
  const out: UnitListRow[] = [];
  for (let i = 0; i < 1500; i += 1) out.push(buildRow(rng, i));
  cache = out;
  return out;
}

function applyFilters(rows: UnitListRow[], q: UnitsQuery): UnitListRow[] {
  // A null on a numeric field means "we don't know" — that's an explicit miss
  // for any range filter the analyst sets, not an implicit pass.
  return rows.filter((r) => {
    if (q.q && !r.address.toLowerCase().includes(q.q.toLowerCase())) return false;
    if (q.postnummer?.length && !q.postnummer.includes(r.postnummer)) return false;
    if (q.m2Min != null && (r.m2 == null || r.m2 < q.m2Min)) return false;
    if (q.m2Max != null && (r.m2 == null || r.m2 > q.m2Max)) return false;
    if (q.roomsMin != null && (r.rooms == null || r.rooms < q.roomsMin)) return false;
    if (q.roomsMax != null && (r.rooms == null || r.rooms > q.roomsMax)) return false;
    if (q.yearMin != null && (r.yearBuilt == null || r.yearBuilt < q.yearMin)) return false;
    if (q.yearMax != null && (r.yearBuilt == null || r.yearBuilt > q.yearMax)) return false;
    if (q.priceMin != null && (r.rentKrPerMonth == null || r.rentKrPerMonth < q.priceMin)) return false;
    if (q.priceMax != null && (r.rentKrPerMonth == null || r.rentKrPerMonth > q.priceMax)) return false;
    if (q.status?.length && !q.status.includes(r.status)) return false;
    if (q.source?.length && !q.source.includes(r.source)) return false;
    return true;
  });
}

function applySort(rows: UnitListRow[], q: UnitsQuery): UnitListRow[] {
  if (!q.sort) return rows;
  const dir = q.dir === 'desc' ? -1 : 1;
  const key = q.sort;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    // Null sorts last regardless of direction — analysts read missing data as
    // "below the fold", not as the largest or smallest extremum.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv), 'da-DK') * dir;
  });
}

export async function fetchUnits(query: UnitsQuery): Promise<UnitsResponse> {
  const all = getAll();
  const filtered = applyFilters(all, query);
  const sorted = applySort(filtered, query);
  const page = Math.max(1, query.page ?? 1);
  const pageSize = Math.max(1, query.pageSize ?? 100);
  const start = (page - 1) * pageSize;
  const rows = sorted.slice(start, start + pageSize);
  // Simulate a small delay so loading states are exercised in dev.
  await new Promise((r) => setTimeout(r, 60));
  return {
    rows,
    total: all.length,
    filtered: filtered.length,
    page,
    pageSize,
    lastSync: new Date().toISOString()
  };
}

export async function fetchUnit(id: string): Promise<UnitListRow | null> {
  const all = getAll();
  await new Promise((r) => setTimeout(r, 40));
  return all.find((r) => r.id === id) ?? null;
}

const ENERGY_RATINGS: EnergyRating[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const BUILDING_TYPES: BuildingType[] = ['apartment', 'townhouse', 'detached', 'semi-detached'];

function buildingKey(row: UnitListRow): string {
  // Drop floor / side from the address to group units into buildings.
  return row.address.replace(/,.*$/, '').toLowerCase();
}

function buildBbr(row: UnitListRow, rng: () => number): BbrAttributes {
  // `row` here is always a buildRow() output, which guarantees the nullable
  // numeric fields are populated for the synthetic dataset. The non-null
  // assertions narrow the API contract back to what this generator emits.
  const floorMatch = row.address.match(/(\d+)\.\s*(tv|th|mf)/);
  const floor = floorMatch ? Number(floorMatch[1]) : null;
  const side = floorMatch ? floorMatch[2] : null;
  return {
    bbrId: `BBR-${row.id.replace('unit-', '')}`,
    buildingId: `BLD-${buildingKey(row).slice(0, 6)}-${row.postnummer}`,
    buildingType: BUILDING_TYPES[Math.floor(rng() * BUILDING_TYPES.length)],
    yearBuilt: row.yearBuilt,
    yearRenovated: rng() > 0.6 ? row.yearBuilt! + Math.floor(20 + rng() * 80) : null,
    m2: row.m2,
    rooms: row.rooms,
    floor,
    side,
    hasBalcony: rng() > 0.45,
    hasElevator: rng() > 0.55,
    energyRating: ENERGY_RATINGS[Math.floor(rng() * ENERGY_RATINGS.length)],
    heating: ['district', 'gas', 'electric', 'other'][Math.floor(rng() * 4)] as BbrAttributes['heating'],
    resolutionConfidence: 0.78 + rng() * 0.22
  };
}

function buildPriceHistory(row: UnitListRow, rng: () => number): PriceHistoryPoint[] {
  // 24 monthly samples ending today. First 12 are "agreed" (historical), next
  // 12 are "asked" (the listing journey). Slightly larger drift than sparkline.
  const points: PriceHistoryPoint[] = [];
  const now = new Date();
  const start = new Date(now.getFullYear() - 2, now.getMonth(), 1);
  let v = row.rentKrPerMonth! * (0.85 + rng() * 0.1);
  for (let i = 0; i < 24; i += 1) {
    const ts = new Date(start.getFullYear(), start.getMonth() + i, 1).toISOString().slice(0, 10);
    const drift = (rng() - 0.45) * v * 0.045;
    v = Math.max(row.rentKrPerMonth! * 0.6, v + drift);
    points.push({
      ts,
      rentKrPerMonth: Math.round(v),
      kind: i < 12 ? 'agreed' : 'asked'
    });
  }
  return points;
}

function buildBuildingContext(row: UnitListRow, rng: () => number): { address: string; unitCount: number; units: BuildingContextUnit[] } {
  const all = getAll();
  const key = buildingKey(row);
  const sameBuilding = all
    .filter((r) => r.id !== row.id && buildingKey(r) === key && r.postnummer === row.postnummer)
    .slice(0, 6);

  // Most synthetic addresses won't actually share buildings; backfill with
  // plausible siblings on the same street so the panel always has data.
  while (sameBuilding.length < 4) {
    const candidate = all[Math.floor(rng() * all.length)];
    if (candidate.id === row.id) continue;
    if (sameBuilding.some((u) => u.id === candidate.id)) continue;
    sameBuilding.push(candidate);
  }

  return {
    address: row.address.replace(/,.*$/, ''),
    unitCount: 6 + Math.floor(rng() * 18),
    units: sameBuilding.slice(0, 6).map((u) => ({
      id: u.id,
      address: u.address,
      m2: u.m2,
      rooms: u.rooms,
      rentKrPerMonth: u.rentKrPerMonth,
      krPerSqm: u.krPerSqm,
      status: u.status
    }))
  };
}

function buildEvents(row: UnitListRow, rng: () => number): UnitEvent[] {
  const events: UnitEvent[] = [];
  const now = Date.now();
  // Listed event
  events.push({
    id: `${row.id}-listed`,
    ts: new Date(now - 1000 * 60 * 60 * 24 * (40 + Math.floor(rng() * 60))).toISOString(),
    kind: 'listed',
    note: `Listed on ${row.source}`,
    source: row.source
  });
  // 1-2 price changes
  const priceChanges = 1 + Math.floor(rng() * 2);
  let lastPrice = row.rentKrPerMonth! + Math.floor(rng() * 1500);
  for (let i = 0; i < priceChanges; i += 1) {
    const newPrice = Math.max(row.rentKrPerMonth! - 500, lastPrice - Math.floor(200 + rng() * 700));
    events.push({
      id: `${row.id}-price-${i}`,
      ts: new Date(now - 1000 * 60 * 60 * 24 * (10 + Math.floor(rng() * 30))).toISOString(),
      kind: 'price_change',
      delta: { from: lastPrice, to: newPrice, field: 'rentKrPerMonth' },
      source: row.source
    });
    lastPrice = newPrice;
  }
  // Optional BBR change
  if (rng() > 0.55) {
    events.push({
      id: `${row.id}-bbr`,
      ts: new Date(now - 1000 * 60 * 60 * 24 * (5 + Math.floor(rng() * 18))).toISOString(),
      kind: 'bbr_change',
      delta: { field: 'energyRating', from: 'D', to: 'C' },
      note: 'Updated from weekly BBR refresh'
    });
  }
  if (row.status === 'rented' || row.status === 'delisted') {
    events.push({
      id: `${row.id}-delisted`,
      ts: new Date(now - 1000 * 60 * 60 * 24 * (1 + Math.floor(rng() * 6))).toISOString(),
      kind: 'delisted',
      note: row.status === 'rented' ? 'Marked as rented' : 'Removed from portal'
    });
  }
  return events.sort((a, b) => b.ts.localeCompare(a.ts));
}

export async function fetchUnitDetail(id: string): Promise<UnitDetailDTO | null> {
  const all = getAll();
  // Provisional ids are synthesised by the address-resolution mock for
  // out-of-scope queries; route them through the same detail builder so the
  // comparables panel exercises both code paths.
  if (id.startsWith('prov-')) {
    const prov = lookupProvisional(id);
    if (!prov) {
      await new Promise((r) => setTimeout(r, 30));
      return null;
    }
    const seed = seedFromString(id);
    const rng = mulberry32(seed);
    const row = provisionalToListRow(prov);
    await new Promise((r) => setTimeout(r, 80));
    return {
      unit: row,
      bbr: buildBbr(row, rng),
      priceHistory: buildPriceHistory(row, rng),
      building: buildBuildingContext(row, rng),
      events: buildEvents(row, rng),
      sources: buildSources(row, rng)
    };
  }
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) {
    await new Promise((r) => setTimeout(r, 30));
    return null;
  }
  const row = all[idx];
  // Per-id deterministic RNG so detail pages don't shimmer between renders.
  const seed = seedFromString(id);
  const rng = mulberry32(seed);
  await new Promise((r) => setTimeout(r, 80));
  return {
    unit: row,
    bbr: buildBbr(row, rng),
    priceHistory: buildPriceHistory(row, rng),
    building: buildBuildingContext(row, rng),
    events: buildEvents(row, rng),
    sources: buildSources(row, rng)
  };
}

// Cross-portal source listings. ~70% of mock units have a single source
// (the one from `row.source`); the remaining 30% have 2-3 portals
// duplicating the listing — that's the cross-portal aggregation case the
// SourcesPanel and divergence panel render.
function buildSources(row: UnitListRow, rng: () => number) {
  const portalSet: Array<{ slug: string; name: UnitListRow['source'] }> = [
    { slug: 'boligportal', name: 'BoligPortal' },
    { slug: 'lejebolig', name: 'Lejebolig' },
    { slug: 'lejeboligportal', name: 'Lejeboligportal' },
    { slug: 'boligzonen', name: 'BoligZonen' }
  ];
  const primary = portalSet.find((p) => p.name === row.source) ?? portalSet[0];
  const portals = [primary];
  if (rng() > 0.7) {
    const extras = portalSet.filter((p) => p.slug !== primary.slug);
    portals.push(extras[Math.floor(rng() * extras.length)]);
    if (rng() > 0.7 && extras.length > 1) {
      const remaining = extras.filter((p) => p.slug !== portals[1].slug);
      portals.push(remaining[Math.floor(rng() * remaining.length)]);
    }
  }
  const baseRent = row.rentKrPerMonth ?? 12000;
  return portals.map((p, i) => {
    // Cross-portal price drift — small variance so the divergence panel has
    // something realistic to highlight without making rows look broken.
    const driftPct = i === 0 ? 0 : (rng() - 0.5) * 0.08;
    const rent = Math.round(baseRent * (1 + driftPct));
    const lastSeenDays = Math.floor(rng() * 14);
    const firstSeenDays = lastSeenDays + Math.floor(rng() * 60) + 1;
    return {
      portalSlug: p.slug,
      portalName: p.name,
      externalId: `${p.slug}-${row.id.slice(0, 8)}-${i}`,
      url: `https://www.${p.slug}.dk/lejebolig/${row.id.slice(0, 8)}-${i}`,
      status: row.status,
      monthlyRentDkk: rent,
      firstSeenAt: new Date(Date.now() - firstSeenDays * 86_400_000).toISOString(),
      lastSeenAt: new Date(Date.now() - lastSeenDays * 86_400_000).toISOString()
    };
  });
}

// --- Comparables (M4) -------------------------------------------------------
//
// The real ranking engine is the Dev Manager's M4 critical-path build. Here
// we mock it with a deterministic scoring function over the same 1500-row
// dataset so the panel renders against realistic spreads and the exclude /
// recompute interaction has something to chew on.

function seedFromString(s: string): number {
  return Array.from(s).reduce((acc, ch) => (acc * 33 + ch.charCodeAt(0)) >>> 0, 5381) >>> 0;
}

function distanceMetres(a: UnitListRow, b: UnitListRow, rng: () => number): number {
  // Without real lat/lng in mocks, derive a stable pseudo-distance from
  // postnummer match + an rng jitter so within-postnummer rows feel close
  // and cross-postnummer feel further. Real metres land with the Dev Manager's
  // PostGIS query.
  const samePost = a.postnummer === b.postnummer;
  const base = samePost ? 80 + rng() * 720 : 1200 + rng() * 4500;
  return Math.round(base);
}

function scoreOne(target: TargetLike, row: UnitListRow, distance: number, rng: () => number): ComparableScoreBreakdown {
  // Mock-built rows always populate m2/rooms/yearBuilt; the M4 BE will carry
  // its own contract reconciliation when it ships (DEV-125).
  const sizeDelta = Math.abs(row.m2! - target.m2) / Math.max(target.m2, 1);
  const sizeMatch = Math.max(0, 1 - sizeDelta * 4); // 25% drift -> 0
  const roomsDelta = Math.abs(row.rooms! - target.rooms);
  const roomsMatch = roomsDelta === 0 ? 1 : roomsDelta === 1 ? 0.7 : 0.35;
  const yearDelta = Math.abs(row.yearBuilt! - target.yearBuilt);
  const yearMatch = Math.max(0, 1 - yearDelta / 60);
  const distScore = Math.max(0, 1 - distance / 5000);
  // Recency: how recently was the comparable listed?
  const ageDays = Math.max(0, (Date.now() - new Date(row.lastSeen).getTime()) / 86400000);
  const recency = Math.max(0, 1 - ageDays / 90);
  // Source quality — a fixed lookup; jitter slightly so the breakdown isn't
  // identical across rows of the same source.
  const sourceQualityBase: Record<UnitListRow['source'], number> = {
    BoligPortal: 0.95,
    Lejebolig: 0.88,
    Lejeboligportal: 0.84,
    BoligZonen: 0.82
  };
  const sourceQuality = Math.max(0, Math.min(1, sourceQualityBase[row.source] - rng() * 0.06));
  const components = {
    sizeMatch: round3(sizeMatch),
    roomsMatch: round3(roomsMatch),
    yearMatch: round3(yearMatch),
    distance: round3(distScore),
    recency: round3(recency),
    sourceQuality: round3(sourceQuality)
  };
  // Published weights — kept in lockstep with the engine config (decision 23
  // in DEV-95 makes weights a config concern; the panel reads the result and
  // stays out of weight policy).
  const total = round3(
    components.sizeMatch * 0.28 +
      components.roomsMatch * 0.18 +
      components.yearMatch * 0.12 +
      components.distance * 0.22 +
      components.recency * 0.1 +
      components.sourceQuality * 0.1
  );
  return { components, total };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function constraintsFor(target: TargetLike, row: UnitListRow): string[] {
  const out: string[] = [];
  const m2Drift = Math.abs(row.m2! - target.m2) / target.m2;
  if (m2Drift <= 0.15) out.push('±15% m²');
  else if (m2Drift <= 0.25) out.push('±25% m²');
  if (Math.abs(row.rooms! - target.rooms) <= 1) out.push('±1 rm');
  if (Math.abs(row.yearBuilt! - target.yearBuilt) <= 25) out.push('±25 yr');
  if (row.postnummer === target.postnummer) out.push('same postnr');
  return out;
}

interface TargetLike {
  m2: number;
  rooms: number;
  yearBuilt: number;
  postnummer: string;
}

function buildComparables(target: ComparablesTarget, anchorRow: UnitListRow | null): ComparableDTO[] {
  const all = getAll();
  const seed = seedFromString(`comparables-${target.unitId}`);
  const rng = mulberry32(seed);
  // Coarse pre-filter so the scoring stage doesn't iterate the whole dataset:
  // ±25% m², ±1 room, ±50 years, exclude the target itself.
  const sizeMin = target.m2 * 0.75;
  const sizeMax = target.m2 * 1.25;
  const candidates = all.filter((r) => {
    if (anchorRow && r.id === anchorRow.id) return false;
    if (r.m2 == null || r.m2 < sizeMin || r.m2 > sizeMax) return false;
    if (r.rooms == null || Math.abs(r.rooms - target.rooms) > 1) return false;
    if (r.yearBuilt == null || Math.abs(r.yearBuilt - target.yearBuilt) > 50) return false;
    if (r.status === 'delisted') return false;
    return true;
  });
  const scored = candidates.map((r) => {
    const distanceM = distanceMetres(anchorRow ?? syntheticAnchorRow(target), r, rng);
    const score = scoreOne(target, r, distanceM, rng);
    return { row: r, distanceM, score };
  });
  scored.sort((a, b) => b.score.total - a.score.total);
  // Cap at 50 like the real engine; UI virtualises but 50 is a sensible
  // analyst-facing ceiling.
  const top = scored.slice(0, 50);
  return top.map((s, i) => ({
    id: `cmp-${target.unitId}-${s.row.id}`,
    unitId: s.row.id,
    rank: i + 1,
    distanceM: s.distanceM,
    address: s.row.address,
    postnummer: s.row.postnummer,
    city: s.row.city,
    m2: s.row.m2!,
    rooms: s.row.rooms!,
    yearBuilt: s.row.yearBuilt!,
    rentKrPerMonth: s.row.rentKrPerMonth!,
    krPerSqm: s.row.krPerSqm!,
    source: s.row.source,
    listedAt: s.row.lastSeen,
    score: s.score,
    constraintsPassed: constraintsFor(target, s.row)
  }));
}

function syntheticAnchorRow(target: ComparablesTarget): UnitListRow {
  return {
    id: target.unitId,
    address: target.address,
    city: target.city,
    postnummer: target.postnummer,
    m2: target.m2,
    rooms: target.rooms,
    yearBuilt: target.yearBuilt,
    rentKrPerMonth: target.rentKrPerMonth,
    krPerSqm: target.krPerSqm,
    status: 'active',
    source: 'BoligPortal',
    lastSeen: new Date().toISOString(),
    spark: []
  };
}

export function aggregateOf(comparables: ComparableDTO[]): ComparablesAggregate {
  if (comparables.length === 0) {
    return { count: 0, median: 0, p25: 0, p75: 0, min: 0, max: 0 };
  }
  const xs = comparables.map((c) => c.krPerSqm).sort((a, b) => a - b);
  const pct = (p: number) => {
    const idx = (xs.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return xs[lo];
    const t = idx - lo;
    return Math.round(xs[lo] * (1 - t) + xs[hi] * t);
  };
  return {
    count: xs.length,
    median: pct(0.5),
    p25: pct(0.25),
    p75: pct(0.75),
    min: xs[0],
    max: xs[xs.length - 1]
  };
}

function targetFromUnit(row: UnitListRow): ComparablesTarget {
  return {
    kind: 'unit',
    unitId: row.id,
    address: row.address,
    postnummer: row.postnummer,
    city: row.city,
    m2: row.m2!,
    rooms: row.rooms!,
    yearBuilt: row.yearBuilt!,
    rentKrPerMonth: row.rentKrPerMonth!,
    krPerSqm: row.krPerSqm!
  };
}

export async function fetchComparables(
  unitId: string,
  query: ComparablesQuery = {}
): Promise<ComparablesResponse | null> {
  let target: ComparablesTarget;
  let anchorRow: UnitListRow | null = null;
  if (unitId.startsWith('prov-')) {
    const prov = lookupProvisional(unitId);
    if (!prov) return null;
    target = prov;
  } else {
    const all = getAll();
    const row = all.find((r) => r.id === unitId);
    if (!row) return null;
    anchorRow = row;
    target = targetFromUnit(row);
  }
  const exclude = new Set(query.excludeIds ?? []);
  const all = buildComparables(target, anchorRow);
  const included = all.filter((c) => !exclude.has(c.id));
  // Re-rank the included set so rank reflects the analyst's current view;
  // excluded comparables are returned with rank=0 so the panel can fold them
  // into a "previously excluded" section without losing their breakdown.
  included.forEach((c, i) => {
    c.rank = i + 1;
  });
  const excluded = all.filter((c) => exclude.has(c.id)).map((c) => ({ ...c, rank: 0 }));
  // Compute time scales gently with the result count so the inline progress
  // bar (decision 10) has something honest to track.
  const computeMs = 90 + Math.min(included.length, 50) * 4;
  await new Promise((r) => setTimeout(r, computeMs));
  return {
    target,
    comparables: [...included, ...excluded],
    aggregate: aggregateOf(included),
    computeMs
  };
}

// --- Address resolution (M4) ------------------------------------------------
//
// The mock recognises three input shapes:
//   1. Free text matching one of the dataset addresses → resolves to that unit.
//   2. Free text containing a 4-digit Danish postnummer in {1620, 2100, …} →
//      resolves to the closest matching unit.
//   3. Anything else → materialises a provisional unit using deterministic
//      fake DAR/BBR data so the comparables flow can be exercised end-to-end.
//
// The real `/api/v1/address/resolve` endpoint owned by the Eager Consultant
// (out-of-scope DAR/BBR fallback, M2) supersedes this mock once wired.

const PHASE1_PRES = new Set(CITY_BY_PRE.map((c) => c.pre));

const provisionalCache = new Map<string, ComparablesTarget>();

function provisionalIdFromAddress(address: string): string {
  return `prov-${seedFromString(address.toLowerCase().trim()).toString(36)}`;
}

function lookupProvisional(id: string): ComparablesTarget | null {
  return provisionalCache.get(id) ?? null;
}

function provisionalToListRow(target: ComparablesTarget): UnitListRow {
  return syntheticAnchorRow(target);
}

function makeProvisional(address: string): ComparablesTarget {
  const id = provisionalIdFromAddress(address);
  const cached = provisionalCache.get(id);
  if (cached) return cached;
  const rng = mulberry32(seedFromString(`prov-anchor-${address}`));
  const m2 = Math.round((54 + rng() * 70) * 10) / 10;
  const rooms = 1 + Math.floor(rng() * 4);
  const yearBuilt = 1900 + Math.floor(rng() * 120);
  const krPerSqm = Math.round(140 + rng() * 140);
  // Detect a 4-digit postnummer fragment in the input. If it matches a phase-1
  // postnummer, we still mark the result provisional because the dataset has
  // no address row matching the input — i.e. the lookup went through the
  // out-of-scope DAR fallback even within phase-1 cities.
  const postMatch = address.match(/\b(\d{4})\b/);
  const post = postMatch ? postMatch[1] : '5600'; // Faaborg-style fallback
  const cityRow = CITY_BY_PRE.find((c) => c.pre === post);
  const reason = PHASE1_PRES.has(post)
    ? 'Address not in DB — resolved via live DAR/BBR'
    : 'Outside Phase 1 cities — resolved via live DAR/BBR';
  const target: ComparablesTarget = {
    kind: 'provisional',
    unitId: id,
    address,
    postnummer: post,
    city: cityRow?.city ?? 'Out-of-scope',
    m2,
    rooms,
    yearBuilt,
    krPerSqm,
    rentKrPerMonth: Math.round(m2 * krPerSqm),
    provisionalReason: reason
  };
  provisionalCache.set(id, target);
  return target;
}

export async function resolveAddress(qRaw: string): Promise<AddressResolveResult | null> {
  const q = qRaw.trim();
  if (!q) return null;
  const all = getAll();
  const lower = q.toLowerCase();
  const exact = all.filter((r) => r.address.toLowerCase().includes(lower));
  await new Promise((r) => setTimeout(r, 60));
  if (exact.length > 0) {
    // Top match is the shortest address that contains the query — proxy for
    // best textual match. Real DAR scoring lands with [DEV-101].
    const sorted = [...exact].sort((a, b) => a.address.length - b.address.length);
    const top = sorted[0];
    return {
      target: targetFromUnit(top),
      candidates: sorted.slice(1, 5).map((u) => ({
        unitId: u.id,
        address: u.address,
        postnummer: u.postnummer,
        city: u.city
      }))
    };
  }
  const provisional = makeProvisional(q);
  return { target: provisional, candidates: [] };
}
